/*
 * Custom gift box builder (retail). See sections/custom-gift-box-builder.liquid
 * for what a box is in the cart and why.
 *
 * Plain ES5, like bulk-gift-boxes.js: a theme asset has no build step.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-custom-gift-box-builder]');
  if (!root || !root.querySelector('[data-cgb-submit]')) return;

  var LABEL_PREFIX = 'Custom Gift Box';

  var config = {
    boxVariantId: Number(root.getAttribute('data-box-variant-id')),
    boxPrice: Number(root.getAttribute('data-box-price')) || 0,
    min: Number(root.getAttribute('data-min-items')) || 2,
    max: Number(root.getAttribute('data-max-items')) || 6,
    currency: root.getAttribute('data-currency') || 'USD',
    cartUrl: root.getAttribute('data-cart-url') || '/cart',
    cartAddUrl: (root.getAttribute('data-cart-add-url') || '/cart/add') + '.js',
    cartUpdateUrl: (root.getAttribute('data-cart-update-url') || '/cart/update') + '.js'
  };

  var nodes = {
    cards: [].slice.call(root.querySelectorAll('[data-cgb-item]')),
    total: root.querySelector('[data-cgb-total]'),
    lines: root.querySelector('[data-cgb-lines]'),
    subtotal: root.querySelector('[data-cgb-subtotal]'),
    hint: root.querySelector('[data-cgb-hint]'),
    error: root.querySelector('[data-cgb-error]'),
    errorText: root.querySelector('[data-cgb-error-text]'),
    submit: root.querySelector('[data-cgb-submit]'),
    submitLabel: root.querySelector('[data-cgb-submit-label]'),
    added: root.querySelector('[data-cgb-added]'),
    addedLabel: root.querySelector('[data-cgb-added-label]'),
    editing: root.querySelector('[data-cgb-editing]'),
    editingLabel: root.querySelector('[data-cgb-editing-label]'),
    dialog: root.querySelector('[data-cgb-dialog]')
  };

  var boxLine = nodes.lines.firstElementChild;
  var addLabel = nodes.submitLabel.textContent;

  // variantId -> how many of it are in the box being built.
  var quantities = {};
  var busy = false;

  // Set when the page was opened to change a box that is already in the cart.
  //   { id, label, lines: { variantId: { key, quantity } },
  //     other: [{ key, title, quantity, price, removed }] }
  // `other` is what the box holds that can't be chosen here any more — sold
  // out, or no longer offered. It stays in the box, counting toward the limit,
  // until the customer takes it out.
  var editing = null;

  /* ---------------------------------------------------------------- helpers */

  var moneyFormat;
  try {
    moneyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: config.currency });
  } catch (err) {
    moneyFormat = null;
  }

  function money(cents) {
    if (moneyFormat) return moneyFormat.format(cents / 100);
    return '$' + (cents / 100).toFixed(2);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function cardInfo(card) {
    var max = card.getAttribute('data-max');
    return {
      variantId: card.getAttribute('data-variant-id'),
      title: card.getAttribute('data-title') || '',
      price: Number(card.getAttribute('data-price')) || 0,
      // Blank means Shopify isn't holding this item to a count.
      stock: max === '' || max === null ? Infinity : Number(max),
      soldOut: card.hasAttribute('data-sold-out')
    };
  }

  function otherUnits() {
    if (!editing) return 0;
    return editing.other.reduce(function (sum, line) {
      return sum + (line.removed ? 0 : line.quantity);
    }, 0);
  }

  function totalUnits() {
    return Object.keys(quantities).reduce(function (sum, id) {
      return sum + quantities[id];
    }, otherUnits());
  }

  function request(url, body) {
    return fetch(url, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    }).then(function (res) {
      return res.json().then(
        function (data) {
          // Shopify reports a refused cart change in the body: { status, description }.
          if (!res.ok || (data && data.status && data.description)) {
            throw new Error((data && (data.description || data.message)) || 'Request failed');
          }
          return data;
        },
        function () {
          throw new Error('Request failed');
        }
      );
    });
  }

  function fetchCart() {
    return request(config.cartUrl + '.js');
  }

  function boxIdOf(item) {
    return (item.properties && item.properties._box_id) || '';
  }

  function isBoxLine(item) {
    return Boolean(item.properties && item.properties._box_label);
  }

  /** "Custom Gift Box 2" when the cart already holds "Custom Gift Box 1". */
  function nextLabel(cart) {
    var used = {};
    (cart.items || []).forEach(function (item) {
      var label = item.properties && item.properties._box_label;
      var match = label && new RegExp('^' + LABEL_PREFIX + ' (\\d+)$').exec(label);
      if (match) used[match[1]] = true;
    });
    var n = 1;
    while (used[n]) n++;
    return LABEL_PREFIX + ' ' + n;
  }

  function newBoxId() {
    var id = '';
    while (id.length < 10) id += Math.random().toString(36).slice(2);
    return id.slice(0, 10);
  }

  /** The header's cart count, from the section HTML a cart call returned. */
  function refreshCartIcon(sections) {
    var html = sections && sections['header-cart-icon'];
    var target = document.getElementById('HeaderCartIcon');
    if (!html || !target) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    var slot = target.querySelector('.shopify-section') || target;
    if (parsed) slot.innerHTML = parsed.innerHTML;
  }

  /* ----------------------------------------------------------------- render */

  function render() {
    var total = totalUnits();
    var subtotal = config.boxPrice;

    nodes.lines.innerHTML = '';
    nodes.lines.appendChild(boxLine);

    nodes.cards.forEach(function (card) {
      var info = cardInfo(card);
      var qty = quantities[info.variantId] || 0;
      var count = card.querySelector('[data-cgb-count]');
      var minus = card.querySelector('[data-cgb-minus]');
      var plus = card.querySelector('[data-cgb-plus]');

      card.classList.toggle('is-picked', qty > 0);
      if (count) {
        count.textContent = String(qty);
        count.hidden = qty < 1;
        count.setAttribute('aria-label', qty + ' in your box');
      }
      if (minus) minus.disabled = busy || qty < 1;
      if (plus) plus.disabled = busy || total >= config.max || qty >= info.stock;

      if (qty > 0) {
        subtotal += info.price * qty;
        addLine((qty > 1 ? qty + ' × ' : '') + info.title, info.price * qty);
      }
    });

    if (editing) {
      editing.other.forEach(function (line, index) {
        if (line.removed) return;
        subtotal += line.price * line.quantity;
        var row = addLine(
          (line.quantity > 1 ? line.quantity + ' × ' : '') + line.title,
          line.price * line.quantity
        );
        var remove = el('button', 'cgb-summary__remove link', 'Remove');
        remove.type = 'button';
        remove.setAttribute('data-cgb-remove-other', String(index));
        remove.setAttribute('aria-label', 'Take ' + line.title + ' out of the box');
        remove.disabled = busy;
        row.firstChild.appendChild(document.createElement('br'));
        row.firstChild.appendChild(el('span', 'cgb-summary__note', 'No longer available. '));
        row.firstChild.appendChild(remove);
      });
    }

    nodes.total.textContent = String(total);
    nodes.subtotal.textContent = money(subtotal);

    var short = config.min - total;
    if (short > 0) {
      nodes.hint.textContent =
        total === 0
          ? 'Choose at least ' + config.min + ' items to get started.'
          : 'Add ' + short + ' more ' + (short === 1 ? 'item' : 'items') + ' to continue.';
    } else if (total >= config.max) {
      nodes.hint.textContent = 'Your box is full.';
    } else {
      var room = config.max - total;
      nodes.hint.textContent = 'Room for ' + room + ' more ' + (room === 1 ? 'item' : 'items') + '.';
    }

    nodes.submit.disabled = busy || total < config.min || total > config.max;
  }

  function addLine(text, cents) {
    var line = el('li', 'cgb-summary__line');
    line.appendChild(el('span', null, text));
    line.appendChild(el('span', null, money(cents)));
    nodes.lines.appendChild(line);
    return line;
  }

  function showError(message) {
    nodes.errorText.textContent = message || '';
    nodes.error.hidden = !message;
  }

  function setBusy(value, label) {
    busy = value;
    nodes.submitLabel.textContent = value && label ? label : editing ? 'Update box' : addLabel;
    render();
  }

  /** Back to choosing: any change to the box makes "is in your cart" stale. */
  function showBuilding() {
    nodes.added.hidden = true;
    nodes.submit.hidden = false;
  }

  /* ------------------------------------------------------------ add to cart */

  function boxItems(id, label, variantIds) {
    return variantIds.map(function (variantId) {
      return {
        id: Number(variantId),
        quantity: quantities[variantId],
        properties: { 'Gift box': label, _box_id: id }
      };
    });
  }

  /**
   * Takes a half-added box back out. Shopify adds a request's items together
   * or not at all, but a box missing its items is the one thing a customer
   * must not be left holding, so this doesn't rely on that.
   */
  function removeBox(id) {
    return fetchCart()
      .then(function (cart) {
        var updates = {};
        (cart.items || []).forEach(function (item) {
          if (boxIdOf(item) === id) updates[item.key] = 0;
        });
        if (Object.keys(updates).length === 0) return null;
        return request(config.cartUpdateUrl, { updates: updates });
      })
      .catch(function () {
        /* nothing more to try */
      });
  }

  function addBox() {
    var picked = Object.keys(quantities).filter(function (id) {
      return quantities[id] > 0;
    });
    var id = newBoxId();
    var label;

    showError('');
    setBusy(true, 'Adding…');

    return fetchCart()
      .then(function (cart) {
        label = nextLabel(cart);
        var items = boxItems(id, label, picked);
        items.push({
          id: config.boxVariantId,
          quantity: 1,
          // The limits travel with the box, so whoever reads the order later
          // (the ops app, before packing) can tell a box that was changed
          // outside them from one built under different ones.
          properties: {
            Box: label,
            _box_label: label,
            _box_id: id,
            _box_min: String(config.min),
            _box_max: String(config.max)
          }
        });
        return request(config.cartAddUrl, { items: items, sections: 'header-cart-icon' });
      })
      .then(function (state) {
        refreshCartIcon(state.sections);
        nodes.addedLabel.textContent = label;
        nodes.added.hidden = false;
        nodes.submit.hidden = true;
        setBusy(false);
      })
      .catch(function (err) {
        return removeBox(id).then(function () {
          setBusy(false);
          showError(addError(err));
        });
      });
  }

  function addError(err) {
    var message = err && err.message;
    if (!message || message === 'Request failed' || /failed to fetch|networkerror/i.test(message)) {
      return "We couldn't add your box to the cart. Please check your connection and try again.";
    }
    return message;
  }

  /* ------------------------------------------------------------- edit a box */

  function loadBoxFromCart(id) {
    return fetchCart().then(function (cart) {
      var lines = (cart.items || []).filter(function (item) {
        return boxIdOf(item) === id;
      });
      var box = lines.filter(isBoxLine)[0];
      if (!box) return;

      var byVariant = {};
      nodes.cards.forEach(function (card) {
        var info = cardInfo(card);
        if (!info.soldOut) byVariant[info.variantId] = true;
      });

      editing = { id: id, label: box.properties._box_label, lines: {}, other: [] };
      quantities = {};
      lines.forEach(function (item) {
        if (isBoxLine(item)) return;
        var variantId = String(item.variant_id);
        if (byVariant[variantId]) {
          editing.lines[variantId] = { key: item.key, quantity: item.quantity };
          quantities[variantId] = item.quantity;
        } else {
          editing.other.push({
            key: item.key,
            title: item.product_title,
            quantity: item.quantity,
            price: item.final_price,
            removed: false
          });
        }
      });

      nodes.editingLabel.textContent = editing.label;
      nodes.editing.hidden = false;
      nodes.submitLabel.textContent = 'Update box';
    });
  }

  function updateBox() {
    var added = [];
    var updates = {};

    Object.keys(quantities).forEach(function (variantId) {
      if (quantities[variantId] > 0 && !editing.lines[variantId]) added.push(variantId);
    });
    Object.keys(editing.lines).forEach(function (variantId) {
      var line = editing.lines[variantId];
      var qty = quantities[variantId] || 0;
      if (qty !== line.quantity) updates[line.key] = qty;
    });
    editing.other.forEach(function (line) {
      if (line.removed) updates[line.key] = 0;
    });

    showError('');
    setBusy(true, 'Updating…');

    // New items go in before anything comes out, so a refused item leaves the
    // box as it was instead of short.
    var adding =
      added.length > 0
        ? request(config.cartAddUrl, { items: boxItems(editing.id, editing.label, added) })
        : Promise.resolve();

    return adding
      .then(function () {
        if (Object.keys(updates).length === 0) return null;
        return request(config.cartUpdateUrl, { updates: updates });
      })
      .then(function () {
        window.location.href = config.cartUrl;
      })
      .catch(function (err) {
        setBusy(false);
        showError(addError(err));
      });
  }

  /* ----------------------------------------------------------------- dialog */

  var lastFocus = null;

  function openDialog(card) {
    var template = card.querySelector('[data-cgb-description]');
    nodes.dialog.querySelector('[data-cgb-dialog-title]').textContent = card.getAttribute('data-title') || '';
    // The store's own product description, as written in Shopify admin.
    nodes.dialog.querySelector('[data-cgb-dialog-body]').innerHTML = template ? template.innerHTML : '';
    lastFocus = document.activeElement;
    nodes.dialog.hidden = false;
    nodes.dialog.querySelector('[data-cgb-dialog-close]').focus();
  }

  function closeDialog() {
    nodes.dialog.hidden = true;
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ----------------------------------------------------------------- events */

  root.addEventListener('click', function (event) {
    var target = event.target;
    if (!target.closest) return;

    var card = target.closest('[data-cgb-item]');
    var step = target.closest('[data-cgb-plus], [data-cgb-minus]');

    if (step && card && !step.disabled) {
      var info = cardInfo(card);
      var qty = quantities[info.variantId] || 0;
      if (step.hasAttribute('data-cgb-plus')) {
        if (totalUnits() < config.max && qty < info.stock) qty += 1;
      } else if (qty > 0) {
        qty -= 1;
      }
      if (qty > 0) quantities[info.variantId] = qty;
      else delete quantities[info.variantId];
      showError('');
      showBuilding();
      render();
      return;
    }

    var removeOther = target.closest('[data-cgb-remove-other]');
    if (removeOther && editing && !busy) {
      var other = editing.other[Number(removeOther.getAttribute('data-cgb-remove-other'))];
      if (other) other.removed = true;
      showError('');
      render();
      return;
    }

    if (card && target.closest('[data-cgb-view]')) {
      openDialog(card);
      return;
    }

    if (target.closest('[data-cgb-dialog-close]') || target === nodes.dialog) {
      closeDialog();
      return;
    }

    if (busy) return;

    if (target.closest('[data-cgb-submit]')) {
      if (editing) updateBox();
      else addBox();
      return;
    }

    if (target.closest('[data-cgb-again]')) {
      showBuilding();
      addBox();
      return;
    }

    if (target.closest('[data-cgb-reset]')) {
      quantities = {};
      showBuilding();
      render();
      window.scrollTo({ top: root.getBoundingClientRect().top + window.pageYOffset - 20, behavior: 'smooth' });
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && !nodes.dialog.hidden) closeDialog();
  });

  /* ------------------------------------------------------------------ start */

  var editId = '';
  try {
    editId = new URLSearchParams(window.location.search).get('box') || '';
  } catch (err) {
    editId = '';
  }

  render();
  if (/^[a-z0-9]{6,20}$/i.test(editId)) {
    setBusy(true);
    loadBoxFromCart(editId)
      .catch(function () {
        /* the box can't be read back: start a new one */
      })
      .then(function () {
        setBusy(false);
      });
  }
})();
